import { IDimPrimitive } from '../plan-primitive';
import { IDimTask, dimTask, layoutPartDims, nearestBox } from './dxf-dim-layout';
import {
    EDxfEntityKind,
    IDxfBounds,
    IDxfEntity,
    IDxfPoint,
    entitiesBounds,
    growBounds,
    translateBounds,
    translateEntity,
} from './dxf-entities';
import { IDxfPaneFit } from './dxf-fit';
import { IDxfSketch, boxesMeet } from './dxf-sketch';

/*
 * A detail view as parts: the pieces of its drawing that touch one another make one part — a
 * stringer of each flight, a step with its treads. Each part takes its dimensions around itself
 * (`layoutPartDims`), then parts whose drawings and dimensions now reach into each other move
 * apart along the line they already stood on, so the rows of one never fall onto the other.
 */

/** Drawing pieces this close on paper, millimetres, belong to one part. */
const PART_TOUCH_MM: number = 1;
/** Air kept between two parts with their dimensions on paper, millimetres. */
const PART_GAP_MM: number = 3;
/** Air between a caption moved beside its part and the part's dimensions, on paper, millimetres. */
const CAPTION_GAP_MM: number = 2;

/** The room a detail view's window gives its drawing, model millimetres. */
export interface IDxfRoom {
    width: number;
    height: number;
}

/** One part of a detail view: its drawing box and everything drawn for it. */
interface IDimPart {
    /** The box of its drawing lines alone: the contour its dimensions stand off from. */
    box: IDxfBounds;
    entities: IDxfEntity[];
}

function centerOf(box: IDxfBounds): IDxfPoint {
    return [(box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2];
}

/** The drawing lines grouped into parts by touch: a union of every pair of boxes that meet. */
function groupShapes(shapes: readonly IDxfEntity[], touch: number): IDxfEntity[][] {
    const boxes: IDxfBounds[] = shapes.map((shape: IDxfEntity): IDxfBounds => growBounds(entitiesBounds([shape]), touch));
    const parent: number[] = shapes.map((_shape: IDxfEntity, index: number): number => index);
    const root: (index: number) => number = (index: number): number => {
        let at: number = index;
        while (parent[at] !== at) {
            at = parent[at];
        }
        return at;
    };
    boxes.forEach((box: IDxfBounds, index: number): void => {
        for (let other: number = index + 1; other < boxes.length; other++) {
            if (boxesMeet(box, boxes[other])) {
                parent[root(other)] = root(index);
            }
        }
    });
    const groups: Map<number, IDxfEntity[]> = new Map<number, IDxfEntity[]>();
    shapes.forEach((shape: IDxfEntity, index: number): void => {
        const key: number = root(index);
        groups.set(key, [...(groups.get(key) ?? []), shape]);
    });
    return [...groups.values()];
}

/** The shift that takes a footprint clear of another, along the axis the two drawings are farther apart on. */
function awayFrom(footprint: IDxfBounds, other: IDxfBounds, box: IDxfBounds, otherBox: IDxfBounds): IDxfPoint {
    const [cx, cy]: IDxfPoint = centerOf(box);
    const [ox, oy]: IDxfPoint = centerOf(otherBox);
    if (Math.abs(cy - oy) >= Math.abs(cx - ox)) {
        return [0, cy < oy ? other.minY - footprint.maxY : other.maxY - footprint.minY];
    }
    return [cx < ox ? other.minX - footprint.maxX : other.maxX - footprint.minX, 0];
}

function shifted(part: IDimPart, [dx, dy]: IDxfPoint): IDimPart {
    return {
        box: translateBounds(part.box, dx, dy),
        entities: part.entities.map((entity: IDxfEntity): IDxfEntity => translateEntity(entity, dx, dy)),
    };
}

/** Moves a part whose footprint runs into an earlier one away from it, along the line their drawings stand on. */
function clearOf(part: IDimPart, earlier: readonly IDimPart[], gap: number): IDimPart {
    let moved: IDimPart = part;
    for (let attempt: number = 0; attempt <= earlier.length; attempt++) {
        const footprint: IDxfBounds = growBounds(entitiesBounds(moved.entities), gap);
        const other: IDimPart | undefined = earlier.find((candidate: IDimPart): boolean =>
            boxesMeet(footprint, entitiesBounds(candidate.entities))
        );
        if (!other) {
            return moved;
        }
        moved = shifted(moved, awayFrom(footprint, entitiesBounds(other.entities), moved.box, other.box));
    }
    return moved;
}

/**
 * A part with its labels and dimensions. A caption standing wholly above the drawing («Марш 1»)
 * would take the room of the dimension rows there; it moves to the left of the dimensioned part
 * instead, level with the drawing. The other labels stay and the numbers keep clear of them.
 */
function dimensionedPart(drawing: IDimPart, labels: readonly IDxfEntity[], tasks: readonly IDimTask[], fit: IDxfPaneFit): IDimPart {
    // A part with no dimensions keeps its captions where they stand.
    const isCaption: (label: IDxfEntity) => boolean = (label: IDxfEntity): boolean =>
        tasks.length > 0 && entitiesBounds([label]).minY >= drawing.box.maxY;
    const captions: IDxfEntity[] = labels.filter(isCaption);
    const inner: IDxfEntity[] = labels.filter((label: IDxfEntity): boolean => !isCaption(label));
    const dimensioned: IDxfEntity[] = [
        ...drawing.entities,
        ...inner,
        ...layoutPartDims(
            tasks,
            fit,
            inner.map((label: IDxfEntity): IDxfBounds => entitiesBounds([label]))
        ),
    ];
    const footprint: IDxfBounds = entitiesBounds(dimensioned);
    const [, middle]: IDxfPoint = centerOf(drawing.box);
    const gap: number = CAPTION_GAP_MM * fit.scale;
    const moved: IDxfEntity[] = captions.map((caption: IDxfEntity, index: number): IDxfEntity => {
        const box: IDxfBounds = entitiesBounds([caption]);
        const lift: number = index * (box.maxY - box.minY + gap);
        return translateEntity(caption, footprint.minX - gap - box.maxX, middle - lift - (box.minY + box.maxY) / 2);
    });
    return { box: drawing.box, entities: [...dimensioned, ...moved] };
}

/**
 * The dimensions without repeats: ГОСТ 2.307 gives a size once, and the sheet puts some twice —
 * the stringer's first tooth gap above and below it. Same part, direction, span and number is a
 * repeat; the same size of another part (the other flight's stringer) is not.
 */
function uniqueTasks(tasks: readonly IDimTask[]): IDimTask[] {
    const seen: Set<string> = new Set<string>();
    return tasks.filter((task: IDimTask): boolean => {
        const ends: string = [task.low, task.high].map((value: number): string => value.toFixed(2)).join('|');
        const key: string = [String(task.part), String(task.axes.along), task.prim.label, ends].join('|');
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

/** Top to bottom, then left to right: the order the parts keep their places in. */
function readingOrder(a: IDimPart, b: IDimPart): number {
    return b.box.maxY - a.box.maxY || a.box.minX - b.box.minX;
}

/** The parts where the sheet put them, each moved off the earlier ones it runs into. */
function asOnSheet(parts: readonly IDimPart[], gap: number): IDimPart[] {
    const placed: IDimPart[] = [];
    for (const part of parts) {
        placed.push(clearOf(part, placed, gap));
    }
    return placed;
}

/** The parts side by side in reading order, their drawings on one bottom line. */
function sideBySide(parts: readonly IDimPart[], gap: number): IDimPart[] {
    const floor: number = Math.min(...parts.map((part: IDimPart): number => part.box.minY));
    let x: number = Math.min(...parts.map((part: IDimPart): number => entitiesBounds(part.entities).minX));
    return parts.map((part: IDimPart): IDimPart => {
        const footprint: IDxfBounds = entitiesBounds(part.entities);
        const moved: IDimPart = shifted(part, [x - footprint.minX, floor - part.box.minY]);
        x += footprint.maxX - footprint.minX + gap;
        return moved;
    });
}

/** How much of the room the parts take: 1 or less fits. */
function overflowOf(parts: readonly IDimPart[], room: IDxfRoom): number {
    const box: IDxfBounds = entitiesBounds(parts.flatMap((part: IDimPart): IDxfEntity[] => part.entities));
    return Math.max((box.maxX - box.minX) / room.width, (box.maxY - box.minY) / room.height);
}

/**
 * The parts arranged in the room: where the sheet put them while that fits; when it does not,
 * side by side if that takes less of the room — two flights' stringers stacked in a wide cell
 * fit only at a smaller scale, side by side they keep it.
 */
function arranged(parts: readonly IDimPart[], gap: number, room: IDxfRoom): IDimPart[] {
    const onSheet: IDimPart[] = asOnSheet(parts, gap);
    if (parts.length < 2 || overflowOf(onSheet, room) <= 1) {
        return onSheet;
    }
    const inRow: IDimPart[] = sideBySide(parts, gap);
    return overflowOf(inRow, room) < overflowOf(onSheet, room) ? inRow : onSheet;
}

/**
 * A detail view's drawing and dimensions in its own millimetres, every dimension in a row of its
 * part by ГОСТ 2.307 and the parts kept apart within `room`, the window's size in model
 * millimetres. A view with no drawing lines keeps its texts only.
 */
export function detailEntities(
    drawing: readonly IDxfEntity[],
    dims: readonly IDimPrimitive[],
    fit: IDxfPaneFit,
    sketch: IDxfSketch,
    room: IDxfRoom
): IDxfEntity[] {
    const shapes: IDxfEntity[] = drawing.filter((entity: IDxfEntity): boolean => entity.kind !== EDxfEntityKind.Text);
    const texts: IDxfEntity[] = drawing.filter((entity: IDxfEntity): boolean => entity.kind === EDxfEntityKind.Text);
    const groups: IDxfEntity[][] = groupShapes(shapes, PART_TOUCH_MM * fit.scale);
    // A view with no dimensions (the steps schema) or no drawing lines keeps the sheet's places.
    if (groups.length === 0 || dims.length === 0) {
        return [...drawing];
    }
    const boxes: IDxfBounds[] = groups.map((group: IDxfEntity[]): IDxfBounds => entitiesBounds(group));
    const labels: IDxfEntity[][] = groups.map((): IDxfEntity[] => []);
    for (const text of texts) {
        const box: IDxfBounds = entitiesBounds([text]);
        labels[nearestBox(centerOf(box), boxes)].push(text);
    }
    const tasks: IDimTask[] = uniqueTasks(dims.map((dim: IDimPrimitive): IDimTask => dimTask(dim, fit, sketch, boxes)));
    const parts: IDimPart[] = groups
        .map((group: IDxfEntity[], index: number): IDimPart =>
            dimensionedPart(
                { box: boxes[index], entities: group },
                labels[index],
                tasks.filter((task: IDimTask): boolean => task.part === index),
                fit
            )
        )
        .sort(readingOrder);
    return arranged(parts, PART_GAP_MM * fit.scale, room).flatMap((part: IDimPart): IDxfEntity[] => part.entities);
}
